/* ============================================================
   model3dView — cc4909b4 (Artem 2026-08-28 §8): pure view
   helpers for the interactive 3D model preview.

   The web viewer fetches raw mesh bytes (GET /models/{id}/content,
   party-scoped) and renders them client-side with three.js; every
   decision below is pure so tests pin the posture without WebGL:

   - which format renders live vs falls back to the ME-2 PNG;
   - the color the mesh is painted with (provider hex swatch
     first, then the shared name→hex fallback, then a neutral);
   - camera fitting math (bounding box → distance for a FOV);
   - loading phases and the support-visualization gate (the
     toggle only appears when support geometry/estimate exists —
     none is produced yet, so it stays hidden, no fake data).

   Pure functions only; Model3DPreview owns the canvas.
   ============================================================ */

import type { ColorSwatch } from '../types/product';
import { fallbackHexFor, isValidHex } from './colorModeView.ts';

/** Mirrors backend FileFormat (numeric on the wire). */
export const MODEL_FORMATS = {
  Stl: 0,
  Obj: 1,
  ThreeMf: 2,
  Step: 3,
} as const;

export type ModelFormatValue = (typeof MODEL_FORMATS)[keyof typeof MODEL_FORMATS];

const FORMAT_BY_NAME: Readonly<Record<string, number>> = {
  Stl: MODEL_FORMATS.Stl,
  Obj: MODEL_FORMATS.Obj,
  ThreeMf: MODEL_FORMATS.ThreeMf,
  Step: MODEL_FORMATS.Step,
};

/**
 * Backend sends the enum as a number; legacy/typed payloads may carry the
 * name. Normalize both (unknown → null → viewer falls back to the PNG).
 */
export function normalizeModelFormat(format: unknown): number | null {
  if (typeof format === 'number' && Number.isInteger(format)
    && format >= 0 && format <= 3) return format;
  if (typeof format === 'string' && FORMAT_BY_NAME[format] != null) {
    return FORMAT_BY_NAME[format];
  }
  return null;
}

/** Mesh formats three.js parses client-side. STEP has no mesh — PNG only. */
export function formatSupports3d(format: number | null | undefined): boolean {
  return format === MODEL_FORMATS.Stl
    || format === MODEL_FORMATS.Obj
    || format === MODEL_FORMATS.ThreeMf;
}

export type ViewerPlan = '3d' | 'png' | 'none';

/**
 * What a preview slot renders: live mesh when the format parses and WebGL
 * is available; the ME-2 PNG when it exists (STEP, no-WebGL devices, load
 * failures reconcile here too); nothing otherwise.
 */
export function resolveViewerPlan(input: {
  format: number | null | undefined;
  previewUrl?: string | null;
  webglOk: boolean;
}): ViewerPlan {
  if (formatSupports3d(input.format) && input.webglOk) return '3d';
  return input.previewUrl ? 'png' : 'none';
}

/** Neutral matte-plastic tone (light theme, no neon). */
export const NEUTRAL_MESH_COLOR = '#8E9AAB';

/** Strict #RRGGBB (backend HexColor contract) — re-export of the shared colorModeView rule. */
export { isValidHex as isValidHexColor } from './colorModeView.ts';

/**
 * The color the mesh renders with: a validated provider hex swatch wins,
 * then the shared name→hex fallback (ac930ee2 — same map the pickers use),
 * then the neutral tone. Never invents a color for unknown names
 * (multicolor jobs render neutral — slots are negotiated in chat per
 * ecfe2257).
 */
export function meshColor(input: {
  hex?: string | null;
  colorName?: string | null;
}): string {
  if (isValidHex(input.hex)) return input.hex!.toUpperCase();
  const mapped = fallbackHexFor(input.colorName);
  if (mapped != null) return mapped;
  return NEUTRAL_MESH_COLOR;
}

/** Resolve the hex swatch for a chosen color name from the line's swatch list. */
export function hexForColorName(
  colorName: string | null | undefined,
  swatches: readonly ColorSwatch[] | null | undefined,
): string | null {
  if (colorName == null || !swatches) return null;
  const hit = swatches.find((s) => s && s.color === colorName);
  return isValidHex(hit?.hexColor) ? hit!.hexColor!.trim().toUpperCase() : null;
}

export type CameraFit = { distance: number; radius: number };

/**
 * Distance that frames a bounding box inside the camera FOV: sphere-fit
 * (half the space diagonal) at distance = radius / sin(fov/2). Pure math —
 * tests pin it with real three.js BufferGeometry boxes.
 */
export function fitCameraDistance(
  size: { x: number; y: number; z: number },
  fovDeg: number,
): CameraFit {
  const radius = 0.5 * Math.sqrt(size.x * size.x + size.y * size.y + size.z * size.z);
  const fov = Math.max(1, Math.min(179, fovDeg)) * (Math.PI / 180);
  return { radius, distance: radius / Math.sin(fov / 2) };
}

export type ViewerPhase = 'idle' | 'loading' | 'ready' | 'fallback';

/** Legal phase transitions (load failure falls back to the PNG, not an error wall). */
export function nextViewerPhase(
  phase: ViewerPhase,
  event: 'start' | 'ready' | 'fail' | 'reset',
): ViewerPhase {
  switch (event) {
    case 'start': return 'loading';
    case 'ready': return phase === 'loading' ? 'ready' : phase;
    case 'fail': return 'fallback';
    case 'reset': return 'idle';
  }
}

/**
 * cc4909b4 §8: the supports toggle is honest — it appears only when real
 * support geometry/estimate data is attached (none exists yet; 7f5734fc
 * will feed it). `null`/`undefined` keep the chip invisible.
 */
export function supportsToggleVisible(
  supports: unknown,
): supports is NonNullable<typeof supports> {
  return supports != null && typeof supports === 'object';
}
